/* PlanCompareTab — side-by-side comparison of two plan iterations.
 *
 * Renders only when there are 2+ snapshots in `/cases/{id}/plan-versions`.
 * The doctor picks "From version" and "To version" — sensible defaults are
 * the previous iteration (left) vs. the newest iteration (right).
 *
 * Changed fields are highlighted amber. Unchanged fields are muted.
 * Designed to read like a clinical diff, not a dev diff.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { GitDiff, Sparkle, Hourglass } from "@phosphor-icons/react";

/* Logical iterations from the snapshot stream. Mirror of the logic inside
   PlanIterationsStack so the v-numbers match the cards above. */
function deriveIterations(versions) {
    const sorted = [...(versions || [])].sort((a, b) => (a.version || 0) - (b.version || 0));
    const iterations = [];
    for (const snap of sorted) {
        if (snap.reason === "sent_for_approval" || snap.reason === "submitted_to_qa" || snap.reason === "planner_resubmit") {
            iterations.push({
                iterationNumber: iterations.length + 1,
                sentAt: snap.created_at,
                plan: snap.plan || {},
                version: snap.version,
            });
        }
    }
    return iterations;
}

/* Field config — drives the comparison table. Keep it clinically meaningful;
   omit anything internal (timestamps, version numbers, snapshot ids). */
const SECTIONS = [
    {
        title: "Treatment overview",
        rows: [
            { key: "external_link",                label: "External plan link",   kind: "link" },
            { key: "aligner_count_upper",          label: "Upper aligners" },
            { key: "aligner_count_lower",          label: "Lower aligners" },
            { key: "estimated_duration_weeks",     label: "Estimated duration (weeks)" },
            { key: "change_interval_days",         label: "Change interval (days)" },
        ],
    },
    {
        title: "Bite metrics",
        rows: [
            { key: "initial_overjet_mm",  label: "Initial overjet (mm)" },
            { key: "final_overjet_mm",    label: "Final overjet (mm)" },
            { key: "initial_overbite_mm", label: "Initial overbite (mm)" },
            { key: "final_overbite_mm",   label: "Final overbite (mm)" },
        ],
    },
    {
        title: "Protocol",
        rows: [
            { key: "total_ipr_mm",          label: "Total IPR (mm)" },
            { key: "proposed_ipr",          label: "IPR plan",          kind: "longtext" },
            { key: "proposed_attachments",  label: "Attachments",        kind: "longtext" },
            { key: "proposed_elastics",     label: "Elastics",           kind: "longtext" },
            { key: "has_overcorrection",    label: "Overcorrection?",    kind: "bool" },
            { key: "overcorrection_notes",  label: "Overcorrection notes", kind: "longtext" },
        ],
    },
    {
        title: "Retention",
        rows: [
            { key: "retainer_type",          label: "Retainer type" },
            { key: "retention_schedule",     label: "Retention schedule", kind: "longtext" },
            { key: "retention_recall_months", label: "Recall (months)" },
        ],
    },
    {
        title: "Planner note",
        rows: [
            { key: "planner_notes", label: "Note from planning team", kind: "longtext" },
        ],
    },
];

function fmtVal(v, kind) {
    if (v === null || v === undefined || v === "") return "—";
    if (kind === "bool") return v ? "Yes" : "No";
    return String(v);
}

function isSame(a, b, kind) {
    if (kind === "bool") return !!a === !!b;
    const na = (a === null || a === undefined || a === "") ? "" : String(a).trim();
    const nb = (b === null || b === undefined || b === "") ? "" : String(b).trim();
    return na === nb;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return "—"; }
}

export default function PlanCompareTab({ caseId }) {
    const [versions, setVersions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fromIdx, setFromIdx] = useState(null);
    const [toIdx, setToIdx] = useState(null);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        api.get(`/cases/${caseId}/plan-versions`)
            .then((r) => { if (mounted) setVersions(r.data || []); })
            .catch(() => { if (mounted) setVersions([]); })
            .finally(() => { if (mounted) setLoading(false); });
    }, [caseId]);

    const iterations = useMemo(() => deriveIterations(versions), [versions]);

    // Sensible defaults: compare last two iterations.
    useEffect(() => {
        if (iterations.length >= 2) {
            setFromIdx((cur) => cur ?? iterations.length - 2);
            setToIdx((cur) => cur ?? iterations.length - 1);
        }
    }, [iterations.length]);

    if (loading) {
        return (
            <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
                <Hourglass size={18} className="inline mr-2" /> Loading plan history…
            </div>
        );
    }

    if (iterations.length < 2) {
        return (
            <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="plan-compare-empty">
                <GitDiff size={28} className="mx-auto text-muted-foreground mb-3" />
                <div className="font-display text-lg font-bold">Only one plan iteration so far</div>
                <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">
                    Comparison becomes available the moment your Invisique™ planning team sends a revised plan based on your feedback.
                </p>
            </div>
        );
    }

    const from = iterations[fromIdx];
    const to = iterations[toIdx];
    const changedCount = SECTIONS.reduce((acc, sec) => acc + sec.rows.filter((r) => !isSame(from?.plan?.[r.key], to?.plan?.[r.key], r.kind)).length, 0);

    return (
        <div className="space-y-5" data-testid="plan-compare-tab">
            {/* Picker bar */}
            <div className="rounded-2xl border border-border bg-card p-4 flex items-center gap-4 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Compare</div>
                <VersionPicker
                    label="From"
                    iterations={iterations}
                    value={fromIdx}
                    onChange={setFromIdx}
                    testId="plan-compare-from"
                />
                <span className="text-muted-foreground">→</span>
                <VersionPicker
                    label="To"
                    iterations={iterations}
                    value={toIdx}
                    onChange={setToIdx}
                    testId="plan-compare-to"
                />
                <div className="flex-1" />
                <div className="text-[11px] text-muted-foreground" data-testid="plan-compare-diff-count">
                    <span className="font-semibold text-amber-700">{changedCount}</span> field{changedCount === 1 ? "" : "s"} changed
                </div>
            </div>

            {/* Sections */}
            {SECTIONS.map((sec) => (
                <CompareSection
                    key={sec.title}
                    title={sec.title}
                    rows={sec.rows}
                    from={from?.plan || {}}
                    to={to?.plan || {}}
                />
            ))}
        </div>
    );
}

function VersionPicker({ label, iterations, value, onChange, testId }) {
    return (
        <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">{label}</span>
            <select
                value={value ?? ""}
                onChange={(e) => onChange(Number(e.target.value))}
                className="rounded-md border border-border bg-card px-2.5 py-1.5 text-sm font-medium"
                data-testid={testId}
            >
                {iterations.map((it, idx) => (
                    <option key={it.version} value={idx}>
                        v{it.iterationNumber} · {fmtDate(it.sentAt)}
                    </option>
                ))}
            </select>
        </label>
    );
}

function CompareSection({ title, rows, from, to }) {
    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`plan-compare-section-${title.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="px-5 py-3 border-b border-border bg-secondary/30 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
                {title}
            </div>
            <div className="divide-y divide-border">
                {rows.map((r) => {
                    const same = isSame(from?.[r.key], to?.[r.key], r.kind);
                    return (
                        <div
                            key={r.key}
                            className={`grid grid-cols-1 sm:grid-cols-3 gap-3 px-5 py-3 text-sm ${same ? "" : "bg-amber-50/50"}`}
                        >
                            <div className="text-muted-foreground text-xs font-semibold sm:text-sm sm:font-normal flex items-center gap-1.5">
                                {r.label}
                                {!same && <Sparkle size={11} weight="fill" className="text-amber-600" />}
                            </div>
                            <CompareCell value={from?.[r.key]} kind={r.kind} muted={same} />
                            <CompareCell value={to?.[r.key]} kind={r.kind} muted={same} highlight={!same} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function CompareCell({ value, kind, muted, highlight }) {
    const v = fmtVal(value, kind);
    if (kind === "link" && value) {
        return (
            <a href={value} target="_blank" rel="noopener noreferrer"
                className={`text-sm break-all ${muted ? "text-muted-foreground" : "text-violet-700 hover:underline"}`}>
                {value}
            </a>
        );
    }
    const cls = highlight
        ? "text-amber-900 font-semibold"
        : muted
            ? "text-muted-foreground"
            : "text-foreground";
    return (
        <div className={`text-sm whitespace-pre-wrap ${cls}`}>{v}</div>
    );
}
